import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Form, Select, Loader } from "semantic-ui-react";
import { getEscalatorVersionOptions, getEscalatorParamFaultDomainOptions } from "../../../utils/pdApiUtils";
import { Button } from "semantic-ui-react";
import { pdApiActions, PdApiActions } from "../../../actions/pdApiActions";
import styles from "./EscalatorDevPackage.module.scss";
import { hasWriteAccess } from "../../../utils/utils";
import * as _ from "lodash";

type StateProps = {
  pdApi: State.PdApi;
  mainState: State.Main;
  isDownloadingEscalatorDevPackage: boolean;
  activeUser?: Api.User;
};

type DispatchProps = {
  pdApiActions: typeof pdApiActions;
};

type OwnProps = {
  isLoadingParameters: boolean;
  isLoadingVersions: boolean;
  isLoadingFaults: boolean;
};

export type EscalatorDevPackageProps = StateProps & DispatchProps & OwnProps;

const EscalatorDevPackage: React.FC<EscalatorDevPackageProps> = (props) => {
  const [domainId, setDomainId] = useState<number>(-1);
  const [versionId, setVersionId] = useState<number>(-1);
  const [allParameters, setAllParameters] = useState<boolean>(true);

  const { pdApi, isDownloadingEscalatorDevPackage, activeUser, isLoadingParameters, isLoadingFaults, isLoadingVersions } = props;
  const { domains, EscalatorVersions, escalatorParameters, escalatorFaults, domainGroups } = pdApi;

  const domainGroupEnumValue = _.values(domainGroups).filter((group: Api.DomainGroup) => group.enumName === "Escalator")[0]?.enumValue;

  const userPermissions = hasWriteAccess(activeUser, "Escalator");

  useEffect(() => {
    if (!EscalatorVersions || _.isEmpty(EscalatorVersions)) {
      props.pdApiActions.fetchEscalatorVersions("Escalator Versions");
    }
    if (!escalatorParameters || escalatorParameters.length === 0) {
      props.pdApiActions.fetchEscalatorParameters();
    }
    if (!escalatorFaults || escalatorFaults.length === 0) {
      props.pdApiActions.fetchEscalatorFaults();
    }
  }, [EscalatorVersions, escalatorParameters, escalatorFaults, props.pdApiActions]);

  const downloadEscDevPackage = () => {
    const input = {
      domainId,
      versionId,
      allParameters,//this is for both param and fault.
      domainName: domains[domainId]?.enumName || "",
      domainGroupEnumValue,
      languages: 3 //hardcoding languages=3 for now, as we only support c and cpp for escalator dev packages
    };
    props.pdApiActions.downloadEscalatorDevPackage(input);
  };
  
  const domainOptions = getEscalatorParamFaultDomainOptions(domains, escalatorParameters, escalatorFaults);// domains of esc parameters and Faults alone
  
  const filterDomainVersions = _.values(EscalatorVersions).filter(// all versions under domains of escalator parameters and faults alone
    (escalatorVersion) => escalatorVersion.domain.id === domainId
  );
  const versionOptions = getEscalatorVersionOptions(domains, filterDomainVersions);
  
  const selectedParameters: Api.EscalatorParameter[] = escalatorParameters.filter(// Get esc parameters of selected domain and version
    (parameter) =>
      versionId === -1
        ? parameter.domain.id === domainId
        : parameter.domain.id === domainId &&
          !!parameter.escalatorParameterBindings.find(
            (binding) => binding.escalatorVersion.id === versionId
          )
  );

  const selectedFaults: Api.EscalatorFault[] = (escalatorFaults ?? []).filter(// Get esc faults of selected domain and version
    (fault) =>
      versionId === -1
        ? fault.domain.id === domainId
        : fault.domain.id === domainId &&
          !!fault.escalatorFaultBindings?.find(
            (binding) => binding.escalatorVersion.id === versionId
          )
  );
  const selectedParametersFaultsCount = (selectedParameters?.length || 0) + (selectedFaults?.length || 0);

  return (
    (isLoadingParameters || isLoadingFaults|| isLoadingVersions) 
    ? (<Loader content="Loading domains and versions" active={true} />) 
    : (
    <>
      <Form.Group>
        <Form>
          <div className="uidropdown">
            <div className={styles.downloadRow}>
              <Form.Field
                id="escalatorDomainDropdown"
                control={Select}
                className={styles.downloadDropdown}
                label="Domain"
                options={domainOptions}
                value={domainId}
                onChange={(_e: React.SyntheticEvent, { value }: { value: number }) => {
                  setDomainId(value);
                  setVersionId(-1);
                  setAllParameters(true);
                }}
                disabled={isDownloadingEscalatorDevPackage || !userPermissions}
              />
            </div>
            <div className={styles.downloadRow}>
              <Form.Field
                id="escalatorVersionsDropdown"
                control={Select}
                className={styles.downloadDropdown}
                label="Version"
                options={versionOptions}
                value={versionId}
                onChange={(_e: React.SyntheticEvent, { value }: { value: number }) => {
                  setVersionId(value);
                  setAllParameters(value === -1 ? true : false);
                }}
                disabled={isDownloadingEscalatorDevPackage || !userPermissions}
              />
            </div>
          </div>
        </Form>
      </Form.Group>
      <>
        {domainId !== -1 && selectedParametersFaultsCount === 0 && (
          <p className="errorMessagePadding">Selected domain and version do not match any parameters or faults.</p>
        )}
        <Button
          id={"downloadEscTextsButton"}
          className={`${
            (selectedParametersFaultsCount === 0 || isDownloadingEscalatorDevPackage || !userPermissions)
              ? styles.disabledButton
              : styles.packageButton
          }`}
          onClick={downloadEscDevPackage}
          disabled={
            (selectedParametersFaultsCount === 0 || isDownloadingEscalatorDevPackage || !userPermissions)
          }
        >
          Create package
        </Button>
        {!userPermissions && <p className="errorMessagePadding">Your role doesn't have neccessary rights.</p>}
      </>
    </> )
  );
};

const mapStateToProps = (state: State.Root) => ({
  mainState: state.main,
  activeUser: state.main.activeUser,
  isDownloadingEscalatorDevPackage: state.escalatorDevPackageStatus.isDownloadingEscalatorDevPackage,
  pdApi: state.pdApi,
  isLoadingParameters: state.loadingStatus.isLoadingEscalatorParameters,
  isLoadingFaults: state.loadingStatus.isLoadingEscalatorFaults,
  isLoadingVersions: state.loadingStatus.isLoadingEscalatorVersions,
});

const mapDispatchToProps = (dispatch: Dispatch<PdApiActions>) => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EscalatorDevPackage);
